// Centralized upload_uuid persistence keyed by File reference.
//
// MN-4800 originally tagged the native File / Blob object with a
// `uploadUuid` property to thread the correlation id through every error
// event. That works because browsers don't freeze File instances today, but
// it has three sharp edges:
//   1. The same File reference can be passed through multiple emitter paths
//      (e.g. dropzone + Borneo); whichever path mutates last wins.
//   2. Some test environments (jsdom strict mode, future browsers) may
//      proxy File and reject extension.
//   3. Linters / static analyzers may flag "non-extensible-object mutation".
//
// A WeakMap keyed by File reference removes those edges: writes are
// idempotent and confined to this module, callers go through a small
// readable API. The legacy `file.uploadUuid` property is read as a
// FALLBACK — emitters that haven't been migrated still work.

const FILE_TO_UPLOAD_UUID = new WeakMap();

const isWeakMapKeyable = value => value !== null && typeof value === "object";

// Records the upload_uuid for a given File / Blob / file-wrapper. Mutates
// `file.uploadUuid` as well so legacy readers (and code paths that haven't
// been migrated to getUploadUuid yet) keep working through the parallel-
// emission window.
//
// @param {File | object | undefined | null} file
// @param {string} uploadUuid
export const setUploadUuid = (file, uploadUuid) => {
  if (!file || !uploadUuid) return;
  if (isWeakMapKeyable(file)) FILE_TO_UPLOAD_UUID.set(file, uploadUuid);
  // Legacy compat: the dropzone tracker still reads file.uploadUuid in
  // getErrorContext. Drop this once every reader uses getUploadUuid.
  try {
    file.uploadUuid = uploadUuid;
  } catch {
    // Some test fixtures freeze the file object; the WeakMap entry is
    // authoritative in that case.
  }
};

// Returns the upload_uuid for a given File, preferring the WeakMap and
// falling back to the legacy `file.uploadUuid` property. Returns undefined
// when neither is set.
//
// @param {File | object | undefined | null} file
// @returns {string | undefined}
export const getUploadUuid = file => {
  if (!file) return undefined;
  if (isWeakMapKeyable(file)) {
    const fromMap = FILE_TO_UPLOAD_UUID.get(file);
    if (fromMap) return fromMap;
  }
  return file.uploadUuid;
};
